// GENERADO: editar packages/backend/src/shared/pricer-datec-cite-formato.ts y ejecutar node scripts/sync-pricer-datec-cite.mjs
// El formato del CITE del Pricer Datec (F4 · T3/T4): puro, sin imports, sin base de datos.
//
// ÚNICA fuente: este archivo. Lo usan el servidor (al asignar el número) y la pantalla de configuración
// (el ejemplo en vivo), esta última con una copia generada (`scripts/sync-pricer-datec-cite.mjs`): lo
// que se ve en la pantalla es lo que se asigna.
//
// El formato es de la EMPRESA y vale hacia adelante: cambiarlo no toca los CITE ya asignados. «Cuándo se
// asigna» NO es una opción (decisión S-4): es al enviar a aprobación, constante en el código.

export type SeparadorCite = '-' | '/' | '.';
export type AnioCite = 'AAAA' | 'AA' | 'NO';
export type ReinicioCite = 'ANUAL' | 'NUNCA';

export interface FormatoCite {
  prefijo: string;
  separador: SeparadorCite;
  anio: AnioCite;
  mes: boolean;
  lob: boolean;
  ejecutivo: boolean;
  /** Cantidad de dígitos del correlativo (relleno con ceros a la izquierda). */
  digitos: number;
  reinicio: ReinicioCite;
  /** Desde qué número arranca la secuencia: nunca baja lo ya usado. */
  primerNumero: number;
}

export const FORMATO_CITE_POR_DEFECTO: Readonly<FormatoCite> = {
  prefijo: 'DATEC-COT',
  separador: '/',
  anio: 'AAAA',
  mes: false,
  lob: true,
  ejecutivo: false,
  digitos: 4,
  reinicio: 'ANUAL',
  primerNumero: 1,
};

/** Lo que cabe en la columna `cite` (VARCHAR(80)). */
export const LARGO_MAXIMO_CITE = 80;

export const PREFIJO_VALIDO = /^[A-Z0-9][A-Z0-9 ._/-]{0,19}$/;
export const SEPARADORES: readonly SeparadorCite[] = ['-', '/', '.'];
export const ANIOS: readonly AnioCite[] = ['AAAA', 'AA', 'NO'];
export const REINICIOS: readonly ReinicioCite[] = ['ANUAL', 'NUNCA'];
export const DIGITOS_MINIMOS = 1;
export const DIGITOS_MAXIMOS = 8;
export const PRIMER_NUMERO_MAXIMO = 99_999_999;

export interface ContextoCite {
  /** Día de referencia, `AAAA-MM-DD` (de ahí salen el año y el mes del número). */
  fecha: string;
  /** Sigla del LOB principal (CSS, SRS…); vacía si no hay. */
  lob: string;
  /** Nombre completo del ejecutivo. */
  ejecutivo: string;
  correlativo: number;
}

/** «Sebastián Saenz» → «SS». Sin tildes, máximo 3 letras. */
export function inicialesDe(nombre: string): string {
  return nombre
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 3)
    .map((p) => p.charAt(0).toUpperCase())
    .join('');
}

/** «LOB CSS» → «CSS». Sólo letras y dígitos, en mayúsculas y hasta 10 caracteres. */
export function siglaDeLob(lob: string): string {
  return lob
    .replace(/^LOB\s+/i, '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9]/g, '')
    .toUpperCase()
    .slice(0, 10);
}

/** El texto del CITE: `DATEC-COT/2026/CSS/0043`. Sin LOB sale `XXX`; sin ejecutivo, `XX`. */
export function formatearCite(f: FormatoCite, ctx: ContextoCite): string {
  const [anio = '', mes = ''] = ctx.fecha.split('-');
  const partes: string[] = [f.prefijo.trim()];
  if (f.anio === 'AAAA') partes.push(anio);
  if (f.anio === 'AA') partes.push(anio.slice(-2));
  if (f.mes) partes.push(mes);
  if (f.lob) partes.push(siglaDeLob(ctx.lob) || 'XXX');
  if (f.ejecutivo) partes.push(inicialesDe(ctx.ejecutivo) || 'XX');
  partes.push(String(Math.max(0, Math.floor(ctx.correlativo))).padStart(f.digitos, '0'));
  return partes.filter((p) => p !== '').join(f.separador);
}

/**
 * La serie de la secuencia: `CITE-2026` si el correlativo reinicia cada año, `CITE` si nunca. El año de la
 * serie es siempre el año completo del día de referencia, se muestre o no en el número.
 */
export function serieDeCite(f: Pick<FormatoCite, 'reinicio'>, fecha: string): string {
  return f.reinicio === 'ANUAL' ? `CITE-${fecha.slice(0, 4)}` : 'CITE';
}

/**
 * Combinaciones que REPETIRÍAN números (el texto es único por empresa): reiniciar cada año sin año en el
 * número, o poner el mes sin año. Devuelve el motivo para mostrarlo a una persona, o `null`.
 */
export function problemaDeRepeticion(
  f: Pick<FormatoCite, 'reinicio' | 'anio' | 'mes'>
): string | null {
  if (f.reinicio === 'ANUAL' && f.anio === 'NO') {
    return 'Si el número vuelve a 1 cada año, tiene que llevar el año: si no, se repetirían números de años anteriores.';
  }
  if (f.mes && f.anio === 'NO') {
    return 'El mes sin el año no alcanza para distinguir un número de otro: incluí también el año.';
  }
  return null;
}

/** La zona horaria del documento y del CITE (D-M): el año y la fecha se leen en hora de Bolivia. */
const ZONA_HORARIA = 'America/La_Paz';

/** El día `AAAA-MM-DD` de un instante, en hora de Bolivia. F6 trae `hoyEnBolivia`; hasta entonces, esto. */
export function diaEnBolivia(instante: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: ZONA_HORARIA,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(instante);
}
